import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

@Directive({
  selector: '[appPinchZoom]',
  standalone: true
})
export class PinchZoomDirective implements OnInit, OnDestroy {
  @Input() minZoom: number = 8;
  @Input() maxZoom: number = 100;
  @Input() currentZoom: number = 16;

  // Emits rapidly during the pinch for UI updates
  @Output() zoomChange = new EventEmitter<number>();
  // Emits once when the user lifts their fingers
  @Output() zoomEnd = new EventEmitter<number>();

  private initialDistance = 0;
  private initialZoom = 0;
  private isPinching = false;
  private latestZoom = 0;
  private animationFrameId: number | null = null;

  private touchStartListener: any;
  private touchMoveListener: any;
  private touchEndListener: any;

  constructor(private el: ElementRef, private ngZone: NgZone) {}

  ngOnInit() {
    // Run outside Angular to prevent triggering Change Detection on every single touch move
    this.ngZone.runOutsideAngular(() => {
      this.touchStartListener = this.onTouchStart.bind(this);
      this.touchMoveListener = this.onTouchMove.bind(this);
      this.touchEndListener = this.onTouchEnd.bind(this);

      // { passive: false } is required to use event.preventDefault() and block the native browser zoom
      this.el.nativeElement.addEventListener('touchstart', this.touchStartListener, { passive: false });
      this.el.nativeElement.addEventListener('touchmove', this.touchMoveListener, { passive: false });
      this.el.nativeElement.addEventListener('touchend', this.touchEndListener);
      this.el.nativeElement.addEventListener('touchcancel', this.touchEndListener);
    });
  }

  ngOnDestroy() {
    this.el.nativeElement.removeEventListener('touchstart', this.touchStartListener);
    this.el.nativeElement.removeEventListener('touchmove', this.touchMoveListener);
    this.el.nativeElement.removeEventListener('touchend', this.touchEndListener);
    this.el.nativeElement.removeEventListener('touchcancel', this.touchEndListener);
    if (this.animationFrameId !== null) cancelAnimationFrame(this.animationFrameId);
  }

  private getDistance(touches: TouchList): number {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private onTouchStart(event: TouchEvent) {
    if (event.touches.length === 2) {
      this.isPinching = true;
      this.initialDistance = this.getDistance(event.touches);
      this.initialZoom = this.currentZoom;
      this.latestZoom = this.currentZoom;

      if (event.cancelable) event.preventDefault();
    }
  }

  private onTouchMove(event: TouchEvent) {
    if (!this.isPinching || event.touches.length !== 2) return;

    // Prevents native scrolling and zooming ONLY during a 2-finger pinch
    if (event.cancelable) event.preventDefault();

    const currentDistance = this.getDistance(event.touches);
    if (this.initialDistance > 0) {
      const scale = currentDistance / this.initialDistance;
      let newZoom = Math.round(this.initialZoom * scale);

      newZoom = Math.max(this.minZoom, Math.min(newZoom, this.maxZoom));

      if (newZoom !== this.latestZoom) {
        this.latestZoom = newZoom;
        this.scheduleZoomUpdate();
      }
    }
  }

  private scheduleZoomUpdate() {
    if (this.animationFrameId === null) {
      this.animationFrameId = requestAnimationFrame(() => {
        this.ngZone.run(() => this.zoomChange.emit(this.latestZoom));
        this.animationFrameId = null;
      });
    }
  }

  private onTouchEnd(event: TouchEvent) {
    if (this.isPinching && event.touches.length < 2) {
      this.isPinching = false;
      // Emit the final value to save settings globally
      this.ngZone.run(() => this.zoomEnd.emit(this.latestZoom));
    }
  }
}
